package dev.youndie.site.pages.blog

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initLogsForAnAgentToReadPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("blog/LogsForAnAgentToRead.md", FrontMatterElement.Builder { addScalar("title", "Logs shaped for an agent to read"); addScalar("searchTitle", "Structured logging for Ktor over MCP, in Kotlin/Native"); addScalar("date", "2026-07-19"); addScalar("description", "Handing a coding agent a dashboard link is handing it nothing. tracy makes MCP the primary read path — and that single decision changes what the storage has to store."); addScalar("library", "tracy"); }))
}

@Page("/blog/logs-for-an-agent-to-read")
@Layout(".components.layouts.MarkdownLayout")
@Composable
fun LogsForAnAgentToReadPage() {
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The way I debug changed before my tools did. A coding agent now reads the stack trace, the diff and the failing test — and then I hand it a Grafana link, which is a URL it cannot see, pointing at a search box it cannot use.")
    }
    org.jetbrains.compose.web.dom.P {
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/tracy") {
            org.jetbrains.compose.web.dom.Text("tracy")
        }
        org.jetbrains.compose.web.dom.Text(" starts from the other end: ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("MCP is the primary read path")
        }
        org.jetbrains.compose.web.dom.Text(", and the UI is the secondary one. That is not a feature bolted on. It changes what has to be stored.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("two-questions-neither-of-which-is-search") }) {
        org.jetbrains.compose.web.dom.Text("Two questions, neither of which is \"search\"")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("During an incident nobody actually wants a text search. They want one of two things.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("What happened to this request.")
        }
        org.jetbrains.compose.web.dom.Text(" You have a ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("traceId")
        }
        org.jetbrains.compose.web.dom.Text(", you want the whole chain: a tree of spans with durations, plus every log line of that request, across every service, in one call. Correlation travels as a standard W3C ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("traceparent")
        }
        org.jetbrains.compose.web.dom.Text(", so tracy interoperates with anything else that propagates it.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("What happened to this order.")
        }
        org.jetbrains.compose.web.dom.Text(" Support never brings you a ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("traceId")
        }
        org.jetbrains.compose.web.dom.Text(". They bring an ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("order_id")
        }
        org.jetbrains.compose.web.dom.Text(". So a field can be marked as an entity key, and then you get the whole history of that entity — across services and across separate traces, including the parts whose log bodies were sampled away.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("That second one is the reason the storage looks the way it does. Searching text cannot answer it, because the traces are unrelated to each other; only the entity ties them together.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("templates-and-data-separated-at-write-time") }) {
        org.jetbrains.compose.web.dom.Text("Templates and data, separated at write time")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A structured log line keeps the message template apart from the values. ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("user {id} denied")
        }
        org.jetbrains.compose.web.dom.Text(" and the id are two different things, stored as two different things.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Do it at write time and you can count exact event frequencies later, group by template, and never confuse what the developer wrote with what a caller supplied. Do it at read time — by parsing the rendered string — and you are reverse-engineering your own format, wrongly, forever.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("FTS5 with the ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("trigram")
        }
        org.jetbrains.compose.web.dom.Text(" tokenizer covers actual text search, plus time-window queries across services, all inside the same binary.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("one-binary-one-file") }) {
        org.jetbrains.compose.web.dom.Text("One binary, one file")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Kotlin/Native for ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("linuxX64")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("linuxArm64")
        }
        org.jetbrains.compose.web.dom.Text(" and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("macosArm64")
        }
        org.jetbrains.compose.web.dom.Text(", with a JVM target for development. Ktor on CIO. SQLite through sqlx4k, with FTS5 and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("contentless_delete")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("No JVM in production, no Elasticsearch, no log shipper. A KMP plugin sits inside your service and that is the whole client side.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("seven-tools-and-a-budget") }) {
        org.jetbrains.compose.web.dom.Text("Seven tools, and a budget")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The MCP surface is seven read-only tools, and the hard part was not the tools.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("An agent has a context window, and a log store can fill it with one query. So there is a ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("hard context budget")
        }
        org.jetbrains.compose.web.dom.Text(", a two-phase content release — metadata first, bodies only when asked for — and a static screen over untrusted text, because log content is attacker- influenced data being handed to a model.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("where-it-actually-is") }) {
        org.jetbrains.compose.web.dom.Text("Where it actually is")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Running on a staging cluster with four services reporting into it, 293 tests, and an agent reading it over MCP.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Two things that number does not cover, stated because a status line listing only wins is not a status line. Those four services are ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("quiet")
        }
        org.jetbrains.compose.web.dom.Text(" — a day of them is on the order of a hundred kilobytes — so the volume figures are still arithmetic rather than measurements, and the sampling behaviour has not been exercised at any interesting scale. And ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("clock_skew_ms")
        }
        org.jetbrains.compose.web.dom.Text(" currently measures the age of a record on arrival rather than the difference between clocks, which a retry inflates.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Source: ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/tracy") {
            org.jetbrains.compose.web.dom.Text("github.com/youndie/tracy")
        }
    }
}
